import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from 'react-fetching-library';
import { Box, Button, IconButton, Tooltip, Typography } from '@shared/ui';
import { EditIcon, PhoneIcon, HomeIcon } from '@shared/ui/icons';
import useMetadataUpdate from '@shared/hooks/useMetadataUpdate';
import getFullName from '@shared/lib/user';
import { isRoutePermitted } from '@shared/lib/isRoutePermitted';
import permissions from '@shared/config/permissions';
import { buildEditPatientPath } from '@entities/patient';
import { openPatientPane, setCurrentPageData, toggleIsInfoPaneExpanded } from 'redux_store';
import { selectCurrentPageContext, selectCurrentPageData } from '../model/selectors';
import { getPatientRows } from '../lib/getPatientRows';
import { openPatientDashboard } from '../lib/openPatientDashboard';
import Avatar from './Avatar';
import InfoRow from './InfoRow';
import SkeletonBody from './SkeletonBody';
import EmptyAnonymous from './EmptyAnonymous';
import ErrorBody from './ErrorBody';

/**
 * Patient sub-page of the InfoPane.
 *
 * Composition (per mockup §3.6):
 *   - Identity block: Avatar (initials) + name (line-clamped to 2) +
 *     MRN (mono) + edit-pencil button.
 *   - Quick actions row: primary Open Dashboard + optional Call icon
 *     (tel: deeplink, only when patient.phone.phone is present).
 *   - Sections from getPatientRows() — Identification / Contact /
 *     Demographics.
 *   - Anonymous patient → EmptyAnonymous placeholder below the sections.
 *
 * Loading: deferred 1.5 s skeleton (legacy behaviour preserved — fast
 * responses never render the skeleton).
 *
 * Error: ErrorBody with a retry callback that re-fires the same fetch.
 */
// MUI's IconButton colours itself from the MUI palette (light only);
// on the dark pane that is a near-black glyph on a near-black button.
// Tokens keep both the icon and the border legible in either theme.
const ACTION_ICON_SX = {
  border: '1px solid var(--qhs-border)',
  borderRadius: '8px',
  color: 'var(--qhs-text-2)',
  '&:hover': {
    color: 'var(--qhs-text-1)',
    backgroundColor: 'var(--qhs-surface-hover)',
    borderColor: 'var(--qhs-border-strong)',
  },
};

const PatientPane = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const i18n = useSelector(state => state.i18n);
  const { infoPane } = i18n;
  const context = useSelector(selectCurrentPageContext) || {};
  const patient = useSelector(selectCurrentPageData);
  const {
    metadata: { currentAccount, currentAccountPermissions, childAccountPermissions },
    isDistributionSponsor,
  } = useSelector(state => state.user);

  const isChildAccount =
    context.accountUUID && context.accountUUID !== currentAccount?.uuid && isDistributionSponsor;

  // Mirror ProtectedRoute's gate exactly so the edit pencil shows precisely
  // when the click would be allowed through. In a DS child-account context the
  // PATIENT_PATCH permission may live on the child account, not the acting DS
  // account — `isRoutePermitted` allows either (same helper the route gate uses).
  const canEdit = isRoutePermitted({
    requiredPermission: permissions.PATIENT_PATCH,
    currentAccountPermissions,
    childAccountPermissions,
    hasChildContext: Boolean(isChildAccount),
  });

  const {
    accountUuid: paramAccountUuid,
    patientUuid: paramsPatientUuid,
    currentAccountUuid,
  } = useParams();

  // Sync URL → pane when the visible patient route changes under us.
  // Mirrors the legacy useEffect at PatientInfo/index.js:83-97.
  useEffect(() => {
    if (
      (paramAccountUuid || currentAccountUuid) &&
      paramsPatientUuid &&
      paramsPatientUuid !== patient?.uuid
    ) {
      dispatch(
        openPatientPane({
          accountUUID: paramAccountUuid || currentAccountUuid,
          patientUUID: paramsPatientUuid,
          patientFirstName: '',
          patientLastName: '',
          notExpand: true,
        })
      );
    }
  }, [paramsPatientUuid]);

  // Patient fetch (same endpoint shape as the legacy PatientInfo).
  const { query, loading, error } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${context.accountUUID}/patients/${context.patientUUID ?? context.uuid}`,
    },
    false,
  ]);

  const fetchPatient = () => {
    query().then(({ error: e, payload }) => {
      if (!e) dispatch(setCurrentPageData(payload));
    });
  };

  // Deferred loader: only show skeleton after 1.5 s so fast responses
  // skip it entirely. Mirrors the legacy canShowLoader timer.
  const [showSkeleton, setShowSkeleton] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => {
    if (query && !patient) {
      setShowSkeleton(false);
      clearTimeout(timerRef.current);
      fetchPatient();
      timerRef.current = setTimeout(() => setShowSkeleton(true), 1500);
    }
    return () => clearTimeout(timerRef.current);
  }, [context]);

  const onOpenDashboard = () =>
    openPatientDashboard({
      dispatch,
      context,
      patient,
      currentAccount,
      isDistributionSponsor,
      i18n,
    });

  const onOpenEdit = () => {
    navigate(
      buildEditPatientPath({
        patientUuid: context.patientUUID ?? context.uuid,
        accountUuid: isChildAccount ? context.accountUUID : null,
        currentAccountUuid: currentAccount.uuid,
      })
    );
    setTimeout(() => dispatch(toggleIsInfoPaneExpanded(false)), 300);
  };

  // Body routing.
  if (error) return <ErrorBody onRetry={fetchPatient} />;
  if (!patient && loading) return showSkeleton ? <SkeletonBody /> : <div />;
  if (!patient) return <div />;

  const sections = getPatientRows({ patient, i18n, isDistributionSponsor });
  const fullName = getFullName(patient ?? context);

  return (
    <Box>
      {/* Identity */}
      <Box sx={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: '14px' }}>
        <Avatar patient={patient} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'var(--qhs-font-head)',
              fontSize: 19,
              fontWeight: 700,
              lineHeight: 1.25,
              letterSpacing: '-0.2px',
              color: 'var(--qhs-text-1)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {fullName}
          </Typography>
          {patient.mrnNumber && (
            <Typography
              sx={{
                fontFamily: 'var(--qhs-font-mono)',
                fontSize: 11.5,
                color: 'var(--qhs-text-2)',
                mt: 0.25,
              }}
            >
              {`MRN ${patient.mrnNumber}`}
            </Typography>
          )}
        </Box>
        {!patient.isAnonymousPatient && canEdit && (
          <Tooltip title={infoPane.editPatient}>
            <IconButton onClick={onOpenEdit} size="small" sx={ACTION_ICON_SX}>
              <EditIcon style={{ width: 14, height: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Quick actions */}
      {!patient.isAnonymousPatient && (
        <Box sx={{ padding: '0 20px 18px', display: 'flex', gap: 1 }}>
          <Button
            variant="primary"
            onClick={onOpenDashboard}
            startIcon={<HomeIcon style={{ width: 16, height: 16 }} />}
            style={{ flex: 1 }}
          >
            {infoPane.openPatientDashboard}
          </Button>
          {patient?.phone?.phone && (
            <Tooltip title={infoPane.callPatient}>
              <IconButton
                onClick={() => {
                  window.location.href = `tel:${patient.phone.phone}`;
                }}
                sx={ACTION_ICON_SX}
              >
                <PhoneIcon style={{ width: 16, height: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Sections */}
      {sections.map(section => (
        <Box key={section.id}>
          <Box
            sx={{
              padding: '14px 20px 8px',
              fontFamily: 'var(--qhs-font-head)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: 'var(--qhs-text-3)',
            }}
          >
            {section.title}
          </Box>
          {section.rows.map(row => (
            <InfoRow key={row.title} {...row} />
          ))}
        </Box>
      ))}

      {/* Anonymous placeholder (rendered after the Identification-only
          section returned by getPatientRows for anonymous payloads). */}
      {patient.isAnonymousPatient && sections.length <= 1 && <EmptyAnonymous />}
    </Box>
  );
};

export default PatientPane;
